import React, { useEffect, useState, useContext } from "react";
import {
  Table,
  Button,
  message,
  Select,
  Popover,
  DatePicker,
  Divider,
  Typography,
  Spin,
} from "antd";

import moment from "moment";

import {
  GetBoatSets,
  GetBoats,
  GetBookings,
  AddNewBooking,
  GetBlockedSlots,
  DeleteBooking,
  GetCMSDocument,
} from "../../../APIManager";

import { UserContext } from "../../../UserContext";

import styles from "./BoatsBooking.module.css";

import BookingRulesCollapse from "./BookingRulesCollapse";
import BookingTimer from "./BookingTimer";

const TimeSlots = [
  "07:00-07:50 AM",
  "08:00-08:50 AM",
  "09:00-09:50 AM",
  "10:00-10:50 AM",
  /*  "11:00-11:50 AM",
  "12:00-12:50 PM",
  "01:00-01:50 PM",
  "02:00-02:50 PM", */
  "03:00-03:50 PM",
  "04:00-04:50 PM",
  "05:00-05:50 PM",
  /*   "06:00-06:50 PM",
  "07:00-07:50 PM", */
];

const PKT_OFFSET = 5; // hours

function toUtcFromPakistanDay(dateLike) {
  return moment(dateLike)
    .utcOffset(PKT_OFFSET, true) // interpret as PKT without shifting clock
    .startOf("day") // 00:00 PKT
    .utc(); // convert to UTC instant
}

const dateFormat = "DD/MM/YYYY";

const { Option } = Select;
const { Title } = Typography;

const BoatsBooking = ({ viewOnly = false }) => {
  const [user, SetUser] = useContext(UserContext);

  const [boatSets, setBoatSets] = useState([]);
  const [boats, setBoats] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [blockedSlots, setBlockedSlots] = useState([]);
  const [selectedDate, setSelectedDate] = useState(
    toUtcFromPakistanDay(moment().utcOffset(PKT_OFFSET))
  );
  const [rules, setRules] = useState({});

  const [loading, setLoading] = useState(false);
  const [inProgressBooking, setInProgressBooking] = useState(false);

  const fetchData = async () => {
    try {
      // Run all requests in parallel
      const [
        resSets,
        resBoats,
        resBookings,
        resBlockedSlots,
        resBoatBookingRules,
      ] = await Promise.all([
        GetBoatSets(),
        GetBoats(),
        GetBookings(),
        GetBlockedSlots(),
        GetCMSDocument("BoatBookingRules"),
      ]);

      const sets = resSets.data;
      const boats = resBoats.data;
      const bookings = resBookings.data;
      const blockedSlots = resBlockedSlots.data;
      const boatBookingRules = resBoatBookingRules.data.Data || {};

      // Map boats into their sets
      const setsWithBoats = sets.map((set) => ({
        ...set,
        boats: boats.filter((boat) => boat.BoatSet._id === set._id),
      }));

      setBookings(bookings);
      setBoatSets(setsWithBoats);
      setBoats(boats);
      setBlockedSlots(blockedSlots);
      setRules(boatBookingRules);
    } catch (err) {
      message.error("Failed to fetch boat sets");
    } finally {
    }
  };

  useEffect(() => {
    setLoading(true);
    fetchData().then(() => {
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    // Fetch immediately on mount
    fetchData();

    // Set interval to fetch every 1 minute
    const interval = setInterval(() => {
      fetchData();
    }, 60 * 1000); // 60 seconds

    // Cleanup on unmount
    return () => clearInterval(interval);
  }, []);

  const todaysBookings = filterBookingsByDay(bookings, selectedDate, "Date");
  const todaysBlockedSlots = filterBookingsByDay(
    blockedSlots,
    selectedDate,
    "Date"
  );

  const onAddSchoolBooking = async (timeSlot, boatId, boatSetId) => {
    if (inProgressBooking) {
      message.warning("Please wait, booking is in progress...");
      return;
    }

    setInProgressBooking(true); // mark booking in progress
    try {
      await AddNewBooking({
        Boat: boatId,
        BoatSet: boatSetId,
        TimeSlot: timeSlot,
        Date: selectedDate,
      });
      message.success("Booking Added Successfully");

      await fetchData(); // refresh data after booking
    } catch (err) {
      message.error(err.response?.data?.error || "Failed to add Booking");
    } finally {
      setInProgressBooking(false); // allow new booking attempts
    }
  };

  const onDeleteBooking = async (bookingId) => {
    try {
      await DeleteBooking({ id: bookingId });
      message.success("Booking Deleted Successfully");
      fetchData();
    } catch (err) {
      message.error(err.response?.data?.error || "Failed to delete booking");
    }
  };

  return (
    <div className={viewOnly ? styles.ViewOnlyContainer : ""}>
      <Divider>
        <Title level={3}>Boat Bookings {viewOnly ? "(View Only)" : ""}</Title>
      </Divider>
      <div className={styles.DateContainer}>
        {/* Left side text */}
        <span className={styles.DateText}>
          {moment(selectedDate).utcOffset(5).format("dddd, MMMM D, YYYY")}
          <div>
            {!loading && selectedDate && rules.AdvanceBookingDays && (
              <BookingTimer
                selectedDate={selectedDate}
                advanceBookingDays={rules.AdvanceBookingDays}
                bookingOpenTime={rules.BookingOpenTime}
                bookingCloseTime={rules.BookingCloseTime}
              />
            )}
          </div>
        </span>

        {/* Right side controls */}
        <div className={styles.DateControls}>
          <Button
            onClick={() =>
              setSelectedDate(
                toUtcFromPakistanDay(
                  moment(selectedDate).utcOffset(PKT_OFFSET).subtract(1, "day")
                )
              )
            }
          >
            Previous
          </Button>

          <Button
            onClick={() =>
              setSelectedDate(
                toUtcFromPakistanDay(moment().utcOffset(PKT_OFFSET))
              )
            }
          >
            Today
          </Button>
          <Button
            onClick={() =>
              setSelectedDate(
                toUtcFromPakistanDay(
                  moment(selectedDate).utcOffset(PKT_OFFSET).add(1, "day")
                )
              )
            }
          >
            Next
          </Button>

          <DatePicker
            value={moment(selectedDate).utcOffset(PKT_OFFSET)}
            onChange={(date) => {
              date && setSelectedDate(toUtcFromPakistanDay(date));
            }}
            format={dateFormat}
            allowClear={false}
          />
        </div>
      </div>
      <br />

      <div style={{ width: "100%" }}>
        {boatSets.length > 0 && (
          <BookingRulesCollapse
            selectedDate={selectedDate}
            advanceBookingDays={rules.AdvanceBookingDays}
            bookingOpenTime={rules.BookingOpenTime}
            bookingCloseTime={rules.BookingCloseTime}
            extraBookingOpenTime={rules.ExtraBookingOpenTime}
          />
        )}
      </div>
      <br />
      <div hidden={!loading} style={{ textAlign: "center", padding: "40px" }}>
        <Spin size="large" tip="Loading boat bookings..." />
        {/*  */}
      </div>
      <div>
        {boatSets.length > 0 && (
          <div>
            {boatSets.map((boatSet) => (
              <BoatSetTable
                boatSet={boatSet}
                bookings={todaysBookings}
                blockedSlots={todaysBlockedSlots}
                onAddSchoolBooking={onAddSchoolBooking}
                onDeleteBooking={onDeleteBooking}
                selectedDate={selectedDate}
                user={user}
                rules={rules}
                boats={boats}
                viewOnly={viewOnly}
                key={boatSet._id}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

function BoatSetTable({
  boatSet,
  bookings,
  blockedSlots,
  onDeleteBooking,
  onAddSchoolBooking,
  selectedDate,
  user,
  rules,
  boats,
  viewOnly,
}) {
  const columns = [
    /* { title: "S.No", dataIndex: "index", width: 60 }, */
    {
      title: "Boat Type",
      dataIndex: "Name",
      width: 180,
      fixed: "left",
      render: (text) => <span style={{ fontWeight: "bold" }}>{text}</span>,
    },

    ...TimeSlots.map((slot) => ({
      title: (
        <div style={{ fontWeight: "bold", textAlign: "center" }}>{slot}</div>
      ),
      dataIndex: slot,
      onCell: (row) => {
        const booking = bookings.find(
          (b) => b.Boat._id === row._id && b.TimeSlot === slot
        );

        const blocked = blockedSlots.find(
          (b) => b.Boat._id === row._id && b.TimeSlot === slot
        );

        const unavailable = row.Unavailable;
        const adminOnlyBooking = row.AdminBookingOnly;

        let cellClass = styles.PopCell; // base class

        if (unavailable) {
          cellClass += ` ${styles.Unavailable}`;
        } else if (booking && user._id === booking.School._id) {
          cellClass += ` ${styles.SelfBooked}`;
        } else if (booking) {
          cellClass += ` ${styles.Booked}`;
        } else if (blocked) {
          cellClass += ` ${styles.Blocked}`;
        } else if (adminOnlyBooking) {
          cellClass += ` ${styles.AdminOnly}`;
        } else {
          cellClass += ` ${styles.Available}`;
        }

        return { className: cellClass };
      },

      render: (value, row) => {
        const booking = bookings.find(
          (b) => b.Boat._id === row._id && b.TimeSlot === slot
        );
        const blocked = blockedSlots.find(
          (b) => b.Boat._id === row._id && b.TimeSlot === slot
        );

        const isSelfBooked = booking && user._id === booking.School._id;

        const unavailable = row.Unavailable;
        const adminOnlyBooking = row.AdminBookingOnly;

        if (unavailable) {
          return (
            <div>
              <div className={styles.SchoolInitials}>Unavailable</div>
            </div>
          );
        }

        if (booking && isSelfBooked) {
          // Popover for booked cell
          const bookedPopContent = (
            <div>
              <p>
                <div>{slot}</div>
                <b>{booking.School.schoolinitials}</b>
              </p>
              <Button
                danger
                size="small"
                onClick={() => {
                  if (isSlotInPast(selectedDate, slot)) {
                    message.error("Cannot delete a slot in the past.");
                    return;
                  }
                  onDeleteBooking(booking._id);
                }}
              >
                Delete Booking
              </Button>
            </div>
          );

          return (
            <Popover trigger="click" content={bookedPopContent} placement="top">
              <div className={styles.SchoolInitials}>
                {booking.School.schoolinitials}
              </div>
            </Popover>
          );
        }

        if (booking && !isSelfBooked) {
          // Popover for booked cell
          const bookedPopContent = (
            <div>
              <p>
                <div>{slot}</div>
                <b>{booking.School.schoolinitials}</b>
              </p>
            </div>
          );

          return (
            <Popover trigger="click" content={bookedPopContent} placement="top">
              <div className={styles.SchoolInitials}>
                {booking.School.schoolinitials}
              </div>
            </Popover>
          );
        }

        if (blocked) {
          // Popover for blocked cell
          const blockedPopover = (
            <div>
              <p>
                <div>{slot}</div>
                <b>{blocked.Reason}</b>
              </p>
            </div>
          );

          return (
            <Popover trigger="click" content={blockedPopover} placement="top">
              <div className={styles.SchoolInitials}>{blocked.Reason}</div>
            </Popover>
          );
        }

        if (adminOnlyBooking) {
          return (
            <div>
              <div>On Request</div>
            </div>
          );
        }

        if (!booking && !blocked && viewOnly) {
          return <div className={styles.Available}>Available</div>;
        }

        if (!booking && !blocked) {
          const availablePopContent = (
            <div>
              <p>
                <div>{slot}</div>
                <b>Available</b>
              </p>
              <Button
                size="small"
                onClick={() => {
                  // First check if the slot is in the past
                  if (isSlotInPast(selectedDate, slot)) {
                    message.error("Cannot book a slot in the past.");
                    return;
                  }

                  const bookingCheck = canBookSlot({
                    selectedTimeSlot: slot,
                    selectedDate: selectedDate,
                    selectedBoat: row._id,
                    advanceBookingDays: rules.AdvanceBookingDays,
                    bookingOpenTime: rules.BookingOpenTime,
                    schoolID: user._id,
                    bookings: bookings,
                    slotExemptions: rules.SlotExemptions,
                    extraBookingOpenTime: rules.ExtraBookingOpenTime,
                    bookingCloseTime: rules.BookingCloseTime,
                    boats: boats,
                  });

                  if (!bookingCheck.canBook) {
                    message.error(bookingCheck.message); // Show the friendly message
                    return;
                  }

                  // If all checks pass, proceed with booking
                  onAddSchoolBooking(slot, row._id, boatSet._id);
                }}
              >
                Book Slot
              </Button>
            </div>
          );

          return (
            <Popover
              trigger="click"
              content={availablePopContent}
              placement="top"
              zIndex={999}
            >
              <div className={styles.Available}>Available</div>
            </Popover>
          );
        }
      },
    })),
  ];

  const data = boatSet.boats.map((boat, i) => ({
    ...boat,
    index: i + 1,
  }));

  return (
    <div>
      <div className={styles.BoatSetName}>{boatSet.Name}</div>
      <Table
        dataSource={data}
        columns={columns}
        pagination={false}
        bordered
        size="small"
        className={styles.disableRowHover}
        scroll={{ x: "750px" }} // <-- horizontal scroll if table too wide
      />
    </div>
    /*  <Card title={`${boatSet.Name}`} style={{ marginBottom: 20 }}>
      <Table
        dataSource={data}
        columns={columns}
        pagination={false}
        bordered
        size="small"
        className={styles.disableRowHover}
      />
    </Card> */
  );
}

export default BoatsBooking;

function isSlotInPast(date, slot) {
  if (!date || !slot) return false;

  const [timeRange, meridiem] = slot.split(" "); // "07:00-07:50 AM" => ["07:00-07:50", "AM"]
  const [startTime] = timeRange.split("-"); // "07:00-07:50" => "07:00"

  let [hours, minutes] = startTime.split(":").map(Number);

  if (meridiem === "PM" && hours < 12) hours += 12;
  if (meridiem === "AM" && hours === 12) hours = 0;

  // Build the slot time as PKT and compare in UTC to avoid local timezone effects
  const slotPkt = moment(date)
    .utc()
    .utcOffset(PKT_OFFSET * 60);
  const slotUtc = slotPkt
    .clone()
    .set({ hour: hours, minute: minutes, second: 0, millisecond: 0 })
    .utc()
    .toDate();

  return slotUtc.getTime() < Date.now();
}
function canBookSlot({
  selectedTimeSlot,
  selectedDate,
  selectedBoat,
  advanceBookingDays,
  bookingOpenTime,
  bookingCloseTime,
  schoolID,
  bookings,
  boats,
  slotExemptions,
  extraBookingOpenTime,
}) {
  if (!selectedDate || !bookingOpenTime) {
    return { canBook: false, message: "Invalid booking date or rules." };
  }

  const now = new Date();
  // PKT day equality for consistent counting and filtering across timezones
  const selectedDayPkt = moment(selectedDate)
    .utc()
    .utcOffset(PKT_OFFSET * 60);
  const schoolIDString = schoolID.toString();
  const isSchoolExempt = slotExemptions.includes(schoolIDString);

  // --- STANDARD TIME-BASED RULES ---

  // Calculate all time boundaries using the new structured helpers
  const openBoundary = getBookingOpenBoundary(
    selectedDate,
    advanceBookingDays,
    bookingOpenTime
  );
  const closeBoundary = getBookingCloseBoundary(
    selectedDate,
    advanceBookingDays,
    bookingCloseTime
  );

  // Rule 1 check setup
  const nextDay = new Date(openBoundary.fullDate);
  // advance one day using UTC setters to avoid local timezone drift
  nextDay.setUTCDate(nextDay.getUTCDate() + 1);

  // RULE 1 — Bookings ONLY allowed on that one day
  if (now < openBoundary.fullDate || now >= nextDay) {
    return {
      canBook: false,
      message: `Bookings for this date are ONLY allowed on ${openBoundary.dateStr}.`,
    };
  }

  // RULE 2 — Cannot book after bookingCloseTime
  if (now > closeBoundary.fullDate) {
    return {
      canBook: false,
      message: `Bookings are closed for today at ${closeBoundary.timeStr}.`,
    };
  }

  // --- BOAT TYPE LIMIT RULE (COUNTING TOTAL BOOKINGS) ---

  // 1. Get the boat type and determine max slots
  const currentBoat = boats.find(
    (b) => b._id.toString() === selectedBoat.toString()
  );
  if (!currentBoat) {
    return { canBook: false, message: "Selected boat not found." };
  }

  const currentBoatType = currentBoat.Type;
  const exemptedBoatTypes = ["Single Scull", "Double Scull"];

  const isExemptBoatType = exemptedBoatTypes.includes(currentBoatType);
  const maxSlots = isSchoolExempt && isExemptBoatType ? 3 : 2;
  const isBookingExtraSlot = maxSlots === 3;

  // 2. Filter existing bookings for the same School, Date, and Boat Type
  const sameTypeBookings = bookings.filter((b) => {
    // Check School ID and Date
    const bPkt = moment(b.Date)
      .utc()
      .utcOffset(PKT_OFFSET * 60);
    const isSameSchoolAndDate =
      b.School._id.toString() === schoolIDString &&
      bPkt.isSame(selectedDayPkt, "day");

    if (!isSameSchoolAndDate) return false;

    // Check Boat Type (with fallback logic)
    const bookedBoatType = b.Boat?.Type;
    let isSameType = bookedBoatType === currentBoatType;
    if (!bookedBoatType) {
      isSameType = boats.some(
        (boat) =>
          boat._id.toString() === b.Boat._id.toString() &&
          boat.Type === currentBoatType
      );
    }
    return isSameType;
  });

  // --- Double-Booking Check ---
  // Ensure the specific boat being requested isn't already booked by the school in this slot
  const isSpecificBoatDoubleBooked = sameTypeBookings.some(
    (b) =>
      b.TimeSlot === selectedTimeSlot &&
      b.Boat._id.toString() === selectedBoat.toString()
  );

  if (isSpecificBoatDoubleBooked) {
    return {
      canBook: false,
      message: `The specific boat (${selectedBoat}) is already booked by your school for the ${selectedTimeSlot} time slot.`,
    };
  }
  // ---------------------------

  // 3. Count the total number of bookings for this boat type.
  const currentBookedCount = sameTypeBookings.length;

  // 4. Check if the booking exceeds the daily limit (based on total boats)
  if (currentBookedCount >= maxSlots) {
    const exemptionMessage = isBookingExtraSlot ? " (exempted limit)" : "";
    return {
      canBook: false,
      message: `Cannot book more than ${maxSlots} total boats of type "${currentBoatType}" per day${exemptionMessage}. You currently have ${currentBookedCount} booked boats.`,
    };
  }

  // 5. Check for the special time restriction for the 3rd slot (if applicable)
  if (isBookingExtraSlot && currentBookedCount === 2 && extraBookingOpenTime) {
    // Use the new helper to calculate the extra open time and get strings
    const extraOpenBoundary = getExtraBookingOpenBoundary(
      selectedDate,
      advanceBookingDays,
      extraBookingOpenTime
    );

    if (now < extraOpenBoundary.fullDate) {
      return {
        canBook: false,
        message: `Your 3rd booking for "${currentBoatType}" can only be made starting on ${extraOpenBoundary.dateStr} at ${extraOpenBoundary.timeStr}.`,
      };
    }
  }

  // All checks passed — allow booking
  return { canBook: true, message: "" };
}

const getRandomStatusClass = () =>
  ["Disabled", "Booked", "Available", "SelfBooked"][
    Math.floor(Math.random() * 4)
  ];

/**
 * Filter objects whose date matches today in local time
 * @param {Array<Object>} arr - Array of objects
 * @param {string} dateProp - Name of the property holding the Date
 * @returns {Array<Object>} Filtered objects for today
 */
function filterBookingsByDay(bookings, selectedDate, dateProp = "Date") {
  if (!selectedDate) return bookings; // Return all if no date is provided
  const targetPkt = moment(selectedDate)
    .utc()
    .utcOffset(PKT_OFFSET * 60);

  return bookings.filter(({ [dateProp]: dateValue }) => {
    const bookingPkt = moment(dateValue)
      .utc()
      .utcOffset(PKT_OFFSET * 60);
    return bookingPkt.isSame(targetPkt, "day");
  });
}

/**
 * Computes the absolute Date object for a booking boundary (open/close/extra time).
 * This rolls back the selectedDate by advanceBookingDays and sets the time.
 * @param {string} selectedDate - The target date for the booking.
 * @param {number} advanceBookingDays - Days prior to selectedDate when booking opens.
 * @param {string} timeString - The time (HH:MM) for the boundary.
 * @returns {Date} The absolute date and time boundary.
 */
export function _getAbsoluteDate(selectedDate, advanceBookingDays, timeString) {
  // Interpret selectedDate as UTC instant representing PKT day; compute boundary in PKT then convert to UTC
  const [hours, minutes] = timeString.split(":").map(Number);
  const basePkt = moment(selectedDate)
    .utc()
    .utcOffset(PKT_OFFSET * 60);
  const boundaryUtc = basePkt
    .clone()
    .subtract(advanceBookingDays, "day")
    .set({ hour: hours, minute: minutes, second: 0, millisecond: 0 })
    .utc()
    .toDate();
  return boundaryUtc;
}

/**
 * Formats a Date object into a readable date string (e.g., "Friday, November 12").
 * @param {Date} date - The Date object to format.
 * @returns {string} The formatted date string.
 */
export function formatDateString(date) {
  // Format consistently in PKT regardless of user locale/timezone
  return moment(date)
    .utc()
    .utcOffset(PKT_OFFSET * 60)
    .format("dddd, MMMM D");
}

/**
 * Formats a Date object into a readable time string (e.g., "8:00 AM").
 * @param {Date} date - The Date object to format.
 * @returns {string} The formatted time string.
 */
export function formatTimeString(date) {
  // Format consistently in PKT regardless of user locale/timezone
  return moment(date)
    .utc()
    .utcOffset(PKT_OFFSET * 60)
    .format("h:mm A");
}

// --- PRIMARY EXTRACTIONS (Following the requested pattern) ---

/**
 * Calculates and formats the standard booking open time boundary.
 */
export function getBookingOpenBoundary(
  selectedDate,
  advanceBookingDays,
  bookingOpenTime
) {
  const fullDate = _getAbsoluteDate(
    selectedDate,
    advanceBookingDays,
    bookingOpenTime
  );
  return {
    dateStr: formatDateString(fullDate),
    timeStr: formatTimeString(fullDate),
    fullDate: fullDate,
  };
}

/**
 * Calculates and formats the standard booking close time boundary.
 */
export function getBookingCloseBoundary(
  selectedDate,
  advanceBookingDays,
  bookingCloseTime
) {
  const fullDate = _getAbsoluteDate(
    selectedDate,
    advanceBookingDays,
    bookingCloseTime
  );
  return {
    dateStr: formatDateString(fullDate), // Date is the same as the open date
    timeStr: formatTimeString(fullDate),
    fullDate: fullDate,
  };
}

/**
 * Calculates and formats the extra booking time boundary for the 3rd slot.
 */
export function getExtraBookingOpenBoundary(
  selectedDate,
  advanceBookingDays,
  extraBookingOpenTime
) {
  const fullDate = _getAbsoluteDate(
    selectedDate,
    advanceBookingDays,
    extraBookingOpenTime
  );
  return {
    dateStr: formatDateString(fullDate),
    timeStr: formatTimeString(fullDate),
    fullDate: fullDate,
  };
}
